import React, {
    useCallback, useEffect, useMemo, useRef, useState,
    FC,
} from 'react';
import update from 'lodash/update';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { contactSelector, schemaSelector, useClientServiceSelector } from 'domain/ClientService';
import Preloader from 'components/Preloader';
import ButtonsBlockRow from 'components/ButtonsBlockRow';
import { createUniqueKey, getClientId } from 'utils';
import { getBearerAccessToken } from 'additiv-services/setup';
import { cloneDeep } from 'lodash/fp';
import { useParams } from 'react-router-dom';
import { FILE_TYPES_ALLOWED, MAXALLOWEDFILES } from 'pages/ClientOnboardingFlow/pages/NewsPlan/UploadDocumentsPage/validation/const';
import { useLayout } from 'prodivers/layout';
import DetailsFormIo from 'components/FormIo';
import { getMatches, removeEmptyObjects } from './utils';

import { Infobox, Modal, notification } from '../../../../ui-library';
import Card from '../../../../components/Card/Card';
import { CUSTOM_SERVICE_LAYER_SERVICES, getCustomServiceLayerBaseUrl } from '../../../../services/constants';
import { useClientProfile } from '../../../../prodivers/clientProfile';
import UploadPersonalDocumentsModal from './components/UploadPersonalDocumentsModal';
import { checkIfEmailIsInUse } from '../../../utils';
import { useManagerProfile } from '../../../../prodivers/managerProfile';
import { ACCESS_POLICY, EMAIL_VALIDATION_REGEX } from '../../../../constants/constants';
import { MailingAddress } from '../../../../components/MailingAddress';
import ServiceManager from '../../../../services/ServiceManager';
import { validateMultipleInputsLength } from '../../../../validation/validation';
import { FIELD_LENGTH } from '../../constants';

import './Details.css';

// --- Interfaces ---

interface RouteParams extends Record<string, string | undefined> {
    dfsClientId?: string;
}

interface PersonalInformation {
    firstName: string;
    lastName: string;
    gender?: string | number;
    dateOfBirth?: string;
    primaryEmail?: string;
    extBlockingCode?: number;
    retirementAgeOn?: number;
    extBlockingReason?: string;
    displayName?: string;
    displayName1?: string;
    isProspect?: boolean;
    identification?: Array<{ countryOfNationality: string }>;
}

interface ContactDetails {
    personalInformation: PersonalInformation;
    relationshipInformation?: any;
    investmentInformation?: any;
    communicationMethods?: {
        mailRecipient?: any;
    };
    [key: string]: any;
}

const Details: FC = () => {
    const { dfsClientId } = useParams<RouteParams>();
    const { i18n: { language }, t } = useTranslation();
    const form = useRef<any>();

    const [submissionData, setSubmissionData] = useState<ContactDetails | null>(null);
    const [isValid, setIsValid] = useState<boolean>(true);
    const [cachedPersonalInfo, setCachedPersonalInfo] = useState<PersonalInformation | null>(null);
    const [showUploadPersonalDocumentsModal, setShowUploadPersonalDocumentsModal] = useState<boolean>(false);
    const [isPostDetailsInProgress, setIsPostDetailsInProgress] = useState<boolean>(false);

    const clientId = getClientId(dfsClientId);
    const { hasAccessPolicy } = useManagerProfile();
    const { setLoadingOverlayActive } = useLayout();
    const queryClient = useQueryClient();

    const {
        data,
        error,
        isLoading,
        getSchema,
        errorLists,
        isLoadingLists,
    } = useClientServiceSelector(schemaSelector);

    const {
        data: contact,
        error: errorContact,
        isLoading: isLoadingContact,
        errorUpdateContact,
        isLoadingUpdateContact,
        updateContact,
    } = useClientServiceSelector(contactSelector);

    const hasAccessToEdit = useMemo(() => hasAccessPolicy(ACCESS_POLICY.contactEdit), [hasAccessPolicy]);

    const oldBaseData = useMemo(() => [
        cachedPersonalInfo?.firstName,
        cachedPersonalInfo?.lastName,
        cachedPersonalInfo?.gender,
        cachedPersonalInfo?.dateOfBirth,
        cachedPersonalInfo?.identification?.[0]?.countryOfNationality,
    ].join(), [cachedPersonalInfo]);

    const { sectionedPortfolios, updateClientInfo } = useClientProfile();

    const hasActivePortfolio = useMemo(() => sectionedPortfolios?.jointRegularPortfolios
        ?.some(port => port?.Status?.value === 'active'), [sectionedPortfolios?.jointRegularPortfolios]);

    const isBaseDataChanged = useCallback(() => {
        const newData = [submissionData?.personalInformation?.firstName,
            submissionData?.personalInformation?.lastName,
            submissionData?.personalInformation?.gender,
            submissionData?.personalInformation?.dateOfBirth,
            submissionData?.personalInformation?.identification?.[0]?.countryOfNationality]?.filter(dta => dta)?.join();

        if (oldBaseData.includes(newData)) {
            return false;
        }

        return true;
    }, [oldBaseData, submissionData]);

    const saveData = useCallback(async () => {
        if (!submissionData) return;
        try {
            delete submissionData?.personalInformation?.displayName;
            delete submissionData?.personalInformation?.displayName1;
            delete submissionData?.personalInformation?.isProspect;
            delete submissionData?.personalInformation?.retirementAgeOn;
            delete submissionData?.relationshipInformation?.contactType;
            delete submissionData?.relationshipInformation?.contactCreateDate;
            delete submissionData?.relationshipInformation?.isActivated;
            delete submissionData?.relationshipInformation?.lastClientLoginDate;
            delete submissionData?.relationshipInformation?.lastClientContactDate;
            delete submissionData?.investmentInformation?.calculatedRiskCategory;
            delete submissionData?.investmentInformation?.chosenRiskCategory;
            delete submissionData?.investmentInformation?.reportingCurrency;
            delete submissionData?.investmentInformation?.performanceSinceInception;
            delete submissionData?.investmentInformation?.performanceYTD;
            delete submissionData?.investmentInformation?.performance1Year;
            delete submissionData?.investmentInformation?.performance3Year;
            delete submissionData?.investmentInformation?.performance5Year;
            delete submissionData?.investmentInformation?.totalValueOfPortfolios;
            delete submissionData?.investmentInformation?.cashComponentOfTotalValue;
            delete submissionData?.investmentInformation?.securitiesComponentOfTotalValue;
            delete submissionData?.investmentInformation?.totalNumberOfPortfolios;
            delete submissionData?.investmentInformation?.activeProducts;

            await updateContact({ params: { details: removeEmptyObjects(submissionData) } });
            setCachedPersonalInfo(cloneDeep(submissionData?.personalInformation));
            notification.open({ content: t('clientDashboard.details.successMessage'), type: 'success' });
            updateClientInfo?.();
            setSubmissionData(null);
        } catch (err: any) {
            setLoadingOverlayActive(false);
            setIsPostDetailsInProgress(false);

            if (err?.error?.response?.status === 409) {
                Modal.error({
                    title: t('clientDashboard.details.generatedDocumentsErrorTitle'),
                    content: t('clientDashboard.details.generatedDocumentsErrorText'),
                    okText: t('Ok'),
                });
            }
        }
    }, [submissionData, updateContact, language]);

    const updateContactDetails = useCallback(async () => {
        if (!submissionData) return;

        setIsPostDetailsInProgress(true);
        setLoadingOverlayActive(true);

        if (!hasAccessToEdit || !submissionData) {
            setLoadingOverlayActive(false);
            setIsPostDetailsInProgress(false);

            return;
        }

        const isLengthValid = validateMultipleInputsLength(
            FIELD_LENGTH.firstAndLastNameCombined,
            'clientValidation.firstAndLastNameLengthCombined',
            submissionData.personalInformation.firstName,
            submissionData.personalInformation.lastName,
        );

        if (isLengthValid) {
            notification.open({ content: t(isLengthValid, { max: FIELD_LENGTH.firstAndLastNameCombined }), type: 'error' });
            setLoadingOverlayActive(false);
            setIsPostDetailsInProgress(false);

            return;
        }

        if (submissionData?.personalInformation?.primaryEmail && !EMAIL_VALIDATION_REGEX.test(submissionData?.personalInformation?.primaryEmail)) {
            notification.open({ content: t('onboardingFlow.personalPage.errorEmailIsSpecialCharacters'), type: 'error' });
            setLoadingOverlayActive(false);
            setIsPostDetailsInProgress(false);

            return;
        }

        if (submissionData?.personalInformation?.primaryEmail && await checkIfEmailIsInUse(clientId, submissionData?.personalInformation?.primaryEmail)) {
            notification.open({ content: t('onboardingFlow.personalPage.errorEmailIsRegister'), type: 'error' });
            setLoadingOverlayActive(false);
            setIsPostDetailsInProgress(false);

            return;
        }

        if (isBaseDataChanged()) {
            setShowUploadPersonalDocumentsModal(true);
            setIsPostDetailsInProgress(false);
            setLoadingOverlayActive(false);

            return;
        }

        await saveData();
        setIsPostDetailsInProgress(false);
        setLoadingOverlayActive(false);
    }, [saveData, isBaseDataChanged, hasAccessToEdit, submissionData, clientId, t, setLoadingOverlayActive]);

    const savePersonalDetails = async () => {
        setIsPostDetailsInProgress(true);
        setLoadingOverlayActive(true);
        await saveData();
        setIsPostDetailsInProgress(false);
        setLoadingOverlayActive(false);
    };

    const onChange = useCallback((data) => {
        if (data) {
            setSubmissionData((obj) => update(obj ?? {}, data.path, () => data.value));
        }
        setIsValid(data?.isValid);
    }, []);

    const modifyLockCodeFields = (component: any) => {
        const blockCode = submissionData?.personalInformation?.extBlockingCode ?? 0;

        if (blockCode >= 7) {
            if (component.key === 'lockCodeAtLPZ') {
                component.defaultValue = true;
                component.label = `${component.label}: `;
            }
            if (component.key === 'extBlockingCode') {
                component.hideLabel = false;
                component.label = `${blockCode} - `;
            }
            if (component.key === 'extBlockingReason') {
                component.label = ` ${submissionData?.personalInformation?.extBlockingReason || ''}`;
                component.hideLabel = false;
            }
        } else {
            if (['extBlockingCode', 'extBlockingReason'].includes(component.key)) {
                component.hidden = true;
            }
            if (component.key === 'lockCodeAtLPZ') {
                component.label = `${component.label}: -`;
            }
        }
    };

    const i18nUpdater = (component: any, fields: any[]) => {
        let { key } = component;

        fields?.forEach((field) => {
            const splittedFieldKey = field.key.split('.');
            const fieldKeySuffix = splittedFieldKey[splittedFieldKey.length - 1];

            if (key === fieldKeySuffix) {
                key = field.key;
            }
        });

        if (component.label) component.label = t(`dynamicCRM.${key}`);
        if (component.title) component.title = t(`dynamicCRM.${key}`);
        if (component.legend) component.legend = t(`dynamicCRM.${key}`);

        if (component.key === 'zurichMA' && !hasAccessPolicy(ACCESS_POLICY.EnableZurichEmployeeToggle)) {
            component.disabled = true;
        }

        modifyLockCodeFields(component);

        component.components?.forEach((cmp: any) => i18nUpdater(cmp, fields));
        component.columns?.forEach((cmp: any) => i18nUpdater(cmp, fields));
    };

    const formIoSchema = useMemo(() => {
        if (!data?.schema) return null;
        let dataReturn: any = null;

        try {
            dataReturn = JSON.parse(data.schema);

            const occupationScheme = getMatches((obj: any) => obj?.properties?.customEnum === 'occupation')(dataReturn);

            if (occupationScheme) {
                occupationScheme.type = 'lazyselect';
                occupationScheme.searchEnabled = true;
                occupationScheme.searchField = 'name';
                occupationScheme.dataSrc = 'url';
                occupationScheme.data = {
                    url: `${getCustomServiceLayerBaseUrl(CUSTOM_SERVICE_LAYER_SERVICES.DEFAULT)}lookup/occupations?language=${language}`,
                    headers: [{ key: 'Authorization', value: getBearerAccessToken() }, { key: 'accept-language', value: language }],
                };
                occupationScheme.limitProperty = 'count';
                occupationScheme.template = '{{ item.name }}';
                occupationScheme.limit = 20;
                occupationScheme.searchResultLimit = 20;
                occupationScheme.idPath = 'id';
                occupationScheme.valueProperty = 'id';
                occupationScheme.authenticate = true;
                occupationScheme.conditional = { when: '', eq: '' };
            }

            dataReturn?.components?.forEach(
                (component: any) => i18nUpdater(component, dataReturn?.metadata?.fields),
            );
        } catch (e) {
            /* Do not throw error */
        }

        return dataReturn;
    }, [data?.schema, language, getBearerAccessToken]);

    const submission = useMemo(() => ({ data: contact?.details }), [contact?.details]);

    useEffect(() => {
        getSchema({ key: 'editCustomer' });
    }, [getSchema]);

    useEffect(() => {
        setCachedPersonalInfo(cloneDeep(contact?.details?.personalInformation));
    }, [contact?.details?.personalInformation]);

    const schema = {
        formIo: formIoSchema, custom: null,
    };

    const errorValidationProperties = errorUpdateContact?.error?.response?.data?.properties?.ValidationErrors;
    const errorValidationsMessages: string[] = Array.isArray(errorValidationProperties)
        ? errorValidationProperties.map((fieldError: any) => fieldError.message) : [];

    const handleSubmitMailingAddress = useCallback(async (mailingData: any, action: number | undefined) => {
        await ServiceManager.customClientService('patchMailingAddress', [{
            contactId: clientId,
            data: mailingData,
            action,
        }]);

        queryClient.setQueryData(['getData_getContact', clientId], (old: any) => ({
            ...old,
            data: {
                ...old?.data,
                details: {
                    ...old?.data?.details,
                    communicationMethods: {
                        ...old?.data?.details?.communicationMethods,
                        mailRecipient: mailingData,
                    },
                },
            },
        }));
    }, [clientId]);

    const onPersonalDocumentsUploadClose = useCallback(() => {
        setShowUploadPersonalDocumentsModal(false);
        setIsPostDetailsInProgress(false);
    }, []);

    const uploadPersonalDocumentsModalResources = useMemo(() => ({
        title: t('clientOverview.personalDocuments.modalTitle'),
        okText: t('clientOverview.personalDocuments.upload'),
        cancelText: t('confirmation.cancel'),
        allowedFileTypesInfo: t('3b.kyc.allowedFormats'),
        sensitiveDataInfo: t('clientOverview.personalDocuments.upload.SensitiveDataInfo'),
    }), [t]);

    return (
        <div className="container Details mb-40">
            <Card>
                <Preloader
                    isLoading={isLoading || isLoadingLists || isLoadingContact}
                    error={error || errorLists || errorContact}
                >
                    <div className="details-form-io">
                        <DetailsFormIo
                            ref={form}
                            schema={schema.formIo}
                            submission={submission}
                            options={{
                                readOnly: false, noAlerts: false, renderMode: 'HWM',
                            }}
                            onChange={onChange}
                            onSubmit={updateContactDetails}
                        />
                    </div>
                    {!isLoadingContact && (
                        <div className="mailing-container">
                            <MailingAddress
                                onSubmit={handleSubmitMailingAddress}
                                initData={{ ...contact?.details?.communicationMethods?.mailRecipient }}
                                isCHOnly={!hasActivePortfolio}
                            />
                        </div>
                    )}

                    <ButtonsBlockRow
                        primaryButton={hasAccessToEdit ? {
                            text: t('clientDetails.save'),
                            loading: isLoadingUpdateContact || isPostDetailsInProgress,
                            disabled: !isValid || isPostDetailsInProgress,
                            onClick: updateContactDetails,
                        } : null}
                    >
                        {!!errorValidationsMessages.length && (
                            <Infobox error >
                                {errorValidationsMessages.map((err, idx) => (
                                    <div key={createUniqueKey(idx, err)}>{err}</div>
                                ))}
                            </Infobox>
                        )}
                    </ButtonsBlockRow>
                </Preloader>
            </Card>
            <UploadPersonalDocumentsModal
                visible={showUploadPersonalDocumentsModal}
                width={640}
                fileTypesAllowed={FILE_TYPES_ALLOWED}
                numberOfFilesAllowed={MAXALLOWEDFILES}
                resouces={uploadPersonalDocumentsModalResources}
                onClose={onPersonalDocumentsUploadClose}
                onUploadSuccess={savePersonalDetails}
                clientId={clientId as number}
            />
        </div>
    );
};

export default Details;
